/* Reusable building blocks for the landing page. */

import React, { useState, useId } from 'react';

const LOGO_MARK = '/logo.svg';
const PHONE_SCREENSHOT = '/img/samplescreen.png';

// ── Phone screenshot — fills the phone frame. `src` overrides per feature.
export function LpPhoneScreenshot({ src = PHONE_SCREENSHOT, alt = 'DoubleOak app preview', loading }) {
  return <img className="lp-phone__shot" src={src} alt={alt} loading={loading}/>;
}

// ── Social handles (extend this list to add platforms)
export const SOCIALS = [
  {
    platform: 'Instagram',
    handle: '@doubleoakapp',
    url: 'https://instagram.com/doubleoakapp',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="5"/>
        <circle cx="12" cy="12" r="4"/>
        <circle cx="17.5" cy="6.5" r="0.7" fill="currentColor"/>
      </svg>
    ),
  },
];

// ── Socials block — parallel CTA to email signup
export function LpSocials({ heading = 'Follow along', align = 'left' }) {
  return (
    <div className={'lp-socials lp-socials--' + align}>
      <span className="lp-socials__head">
        <span className="lp-socials__head-rule" aria-hidden="true"></span>
        {heading}
      </span>
      <div className="lp-socials__row">
        {SOCIALS.map((s) => (
          <a
            key={s.platform}
            className="lp-social-pill"
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${s.platform} ${s.handle}`}
          >
            <span className="lp-social-pill__icon">{s.icon}</span>
            <span className="lp-social-pill__handle">
              <span className="lp-social-pill__platform">{s.platform}:</span>
              {' '}{s.handle}
            </span>
            <span className="lp-social-pill__arrow" aria-hidden="true">↗</span>
          </a>
        ))}
      </div>
    </div>
  );
}

// ── Brand lockup (used by the nav)
export function LpLockup() {
  return (
    <div className="lp-nav__lockup">
      <img src={LOGO_MARK} alt="" />
      <span className="word">Double<em>Oak</em></span>
    </div>
  );
}

// ── Email signup — posts to Loops, falls through to a "submitted" state
const LOOPS_FORM_ID = import.meta.env.VITE_LOOPS_FORM_ID;

export function LpSignup({ ctaLabel = 'Join the waitlist' }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle');
  const [err, setErr] = useState('');
  const inputId = useId();
  const errId = `${inputId}-err`;
  const noticeId = `${inputId}-notice`;

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const onEmailChange = (e) => {
    setEmail(e.target.value);
    if (state === 'err') setState('idle');
  };

  const onEmailBlur = () => {
    const v = email.trim();
    if (!v) return;
    if (!isValidEmail(v)) {
      setState('err');
      setErr("That doesn't look like a valid email.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!v) { setState('err'); setErr('Please enter your email.'); return; }
    if (!isValidEmail(v)) { setState('err'); setErr("That doesn't look like a valid email."); return; }

    if (!LOOPS_FORM_ID) {
      console.warn('VITE_LOOPS_FORM_ID is not set — skipping Loops API call.');
      setState('done');
      return;
    }

    setState('submitting');
    try {
      const res = await fetch(`https://app.loops.so/api/newsletter-form/${LOOPS_FORM_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: v, source: 'landing-page' }).toString(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        setState('err');
        setErr(data.message || "Couldn't sign you up. Try again in a moment.");
        return;
      }
      setState('done');
    } catch {
      setState('err');
      setErr("Network error. Check your connection and try again.");
    }
  };

  if (state === 'done') {
    return (
      <div className="lp-ticket lp-ticket--v2" role="status" aria-live="polite">
        <div className="lp-ticket__check" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <div className="lp-ticket__body">
          <div className="lp-ticket__title">You're <em>in</em>.</div>
        </div>
      </div>
    );
  }

  const isErr = state === 'err';
  const isSubmitting = state === 'submitting';
  return (
    <form className={'lp-signup-v2' + (isErr ? ' lp-signup-v2--err' : '')} onSubmit={submit} noValidate>
      <label htmlFor={inputId} className="lp-visually-hidden">Email address</label>
      <div className="lp-signup-v2__row">
        <input
          id={inputId}
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={onEmailChange}
          onBlur={onEmailBlur}
          autoComplete="email"
          inputMode="email"
          required
          aria-required="true"
          aria-invalid={isErr || undefined}
          aria-describedby={[isErr ? errId : null, noticeId].filter(Boolean).join(' ') || undefined}
        />
        <button type="submit" className="lp-cta-primary" disabled={isSubmitting} aria-busy={isSubmitting || undefined}>
          {isSubmitting ? 'Submitting…' : ctaLabel}
          <span aria-hidden="true">→</span>
        </button>
      </div>
      <div className="lp-signup-v2__err" id={errId} role="alert" aria-live="assertive">
        {isErr && (
          <>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {err}
          </>
        )}
      </div>
      <p className="lp-signup-v2__meta">Closed beta · June 2026 · 21+</p>
      <p id={noticeId} className="lp-signup-v2__notice-body">
        We don't share your email. <a href="/privacy">Privacy Policy</a>.
      </p>
    </form>
  );
}

// ── Footer
export function LpFooter() {
  return (
    <footer className="lp-foot" role="contentinfo">
      <div className="lp-foot__brand">
        <div className="word">Double<em>Oak</em></div>
        <p>Built by whiskey enthusiasts, in San Antonio.</p>
        <address className="lp-foot__addr">
          DoubleOak Labs LLC<br/>
          5900 Balcones Drive STE 100<br/>
          Austin, TX 78731
        </address>
      </div>
      <div>
        <h2 className="lp-foot__h">Product</h2>
        <ul><li><a href="#feed">Feed</a></li><li><a href="#bounties">Bounties</a></li><li><a href="#squads">Squads</a></li><li><a href="#map">Hunt Map</a></li><li><a href="#faq">FAQ</a></li></ul>
      </div>
      <div>
        <h2 className="lp-foot__h">Reach</h2>
        <ul>
          <li><a href="mailto:hello@doubleoak.app">hello@doubleoak.app</a></li>
          <li><a href="mailto:legal@doubleoak.app">legal@doubleoak.app</a></li>
          {SOCIALS.map((s) => (
            <li key={s.platform}><a href={s.url} target="_blank" rel="noopener noreferrer">{s.platform}</a></li>
          ))}
        </ul>
      </div>
      <div>
        <h2 className="lp-foot__h">Legal</h2>
        <ul>
          <li><a href="/privacy">Privacy Policy</a></li>
          <li><a href="/terms">Terms of Service</a></li>
          <li><a href="/privacy#your-rights">Your privacy rights</a></li>
          <li><a href="/privacy#do-not-sell">Do Not Sell or Share My Personal Information</a></li>
        </ul>
      </div>
      <div className="lp-foot__bottom">
        <span>© 2026 DoubleOak Labs LLC. All rights reserved.</span>
        <span>San Antonio · Texas, USA</span>
        <span>Drink responsibly · 21+ only</span>
      </div>
    </footer>
  );
}
